import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageHeaderComponent } from '../../shared/components/ui-shell.component';
import { MonitoringService } from '../../services/monitoring.service';
import { Station } from '../../models/monitoring.models';
import { APP_VERSION, BUILD_SHA } from '../../../environments/version';

@Component({
  selector: 'app-station-select',
  standalone: true,
  imports: [FormsModule, PageHeaderComponent],
  template: `
    <div class="screen">
      <app-page-header title="VisuParc" />

      <div class="content">
        @if (!searchOpen()) {
          <button type="button" class="select-trigger" (click)="openSearch()">
            {{ selectedLabel() }}
          </button>
        } @else {
          <div class="search-panel">
            <input
              type="text"
              class="search-input"
              placeholder="Rechercher une gare..."
              [ngModel]="query()"
              (ngModelChange)="onQueryChange($event)"
              autofocus
            />
            <ul class="suggestions">
              @for (station of filtered(); track station.id) {
                <li>
                  <button type="button" class="suggestion-item" (click)="pick(station)">
                    {{ station.name }}
                  </button>
                </li>
              }
            </ul>
          </div>
        }
      </div>

      <!-- Demande du testeur ([DAT TER] IHM ASCT) : savoir « où on en est » sans avoir à
           ouvrir GitHub. Deux informations, pas une : la release répond à la question posée,
           le SHA identifie l'artefact exact — et leur désaccord signale un bump de version
           oublié, que la release seule masquerait. -->
      <footer class="app-version">
        @if (version) {
          Version {{ version }} <span class="version-sep" aria-hidden="true">·</span>
        }
        <span class="build-sha">{{ sha || 'build inconnu' }}</span>
      </footer>
    </div>
  `,
  styles: [
    `
      /* L'hote porte la hauteur de l'ecran. Sans ca il reste en display:inline,
         le min-height de .screen ne se resout sur rien, et le footer remonte se
         coller sous le contenu au lieu de tenir le bas de l'ecran. */
      :host {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        min-height: 100dvh;
      }

      .screen {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .content {
        padding: 1.5rem 1rem;
        flex: 1;
      }

      .select-trigger {
        width: 100%;
        text-align: left;
        padding: 0.85rem 1rem;
        border: 1px solid #888;
        border-radius: 999px;
        background: #fff;
        font-size: 1rem;
        color: #666;
        cursor: pointer;
      }

      .search-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #888;
        border-radius: 8px;
        font-size: 1rem;
        box-sizing: border-box;
        background: #fff;
        color: #222;
      }

      .suggestions {
        list-style: none;
        margin: 0.5rem 0 0;
        padding: 0;
        border: 1px solid #ccc;
        border-radius: 8px;
        overflow: hidden;
      }

      .suggestion-item {
        width: 100%;
        text-align: left;
        padding: 0.85rem 1rem;
        border: none;
        border-bottom: 1px solid #eee;
        background: #fff;
        color: #222;
        font-size: 1rem;
        cursor: pointer;
      }

      .suggestion-item:hover {
        background: #f5f5f5;
      }

      .suggestions li:last-child .suggestion-item {
        border-bottom: none;
      }

      .app-version {
        margin-top: auto;
        padding: 0.75rem 1rem 1rem;
        /* En bas à DROITE, comme demandé en recette. */
        text-align: right;
        font-size: 0.75rem;
        color: #9e9e9e;
        /* Sélectionnable : on la recopie dans les tickets. */
        user-select: text;
      }

      .version-sep {
        margin: 0 0.15rem;
      }

      .build-sha {
        font-variant-numeric: tabular-nums;
      }
    `,
  ],
})
export class StationSelectComponent {
  private readonly monitoring = inject(MonitoringService);
  private readonly router = inject(Router);

  /** Tag de la release déployée, ex. « v0.0.14 ». Voir src/environments/version.ts. */
  readonly version = APP_VERSION;

  /** SHA court du commit construit. Affiché seul tant qu'aucune release n'a été taggée. */
  readonly sha = BUILD_SHA;

  readonly searchOpen = signal(false);
  readonly query = signal('');
  readonly filtered = signal<Station[]>([]);

  selectedLabel(): string {
    return 'Sélectionner une gare';
  }

  openSearch(): void {
    this.searchOpen.set(true);
    this.monitoring.getStations().subscribe((stations) => this.filtered.set(stations));
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.monitoring.searchStations(value).subscribe((stations) => this.filtered.set(stations));
  }

  pick(station: Station): void {
    this.router.navigate(['/gare', station.id, 'historique']);
  }
}
